import { Chip, Group, Stack } from "@tine/designsystem-ui-react";
import isNil from "lodash-es/isNil";
import { observer } from "mobx-react-lite";
import { useParams } from "wouter";

import { isDataAvailable } from "../../common/utils/asyncDataUtils";

import contentStore from "../../stores/cms/contentStore";
import segmentStore from "../../stores/cms/segmentStore";
import { WouterLinkWithTracking } from "../Links";

type SectionProps = {
   label: string;
   active: boolean;
   url: string;
};

const sectionHasContent = (sectionId: string) => {
   return contentStore.urlData.data.some(
      (urlData) =>
         urlData.visibleInSections?.includes(sectionId) &&
         (urlData.type !== "v2_article" || segmentStore.isArticleVisible(urlData.id))
   );
};

const ChipLink = observer(({ active, label, url }: SectionProps) => (
   <div>
      <WouterLinkWithTracking trackingLocationName="Section navigation" to={url} linkName={label} className="no-underline">
         <Chip checked={active}>{label}</Chip>
      </WouterLinkWithTracking>
   </div>
));

const SectionNavigator = () => {
   const { fullUrl } = useParams();
   const rootNodeSlug = fullUrl?.split("/")[0];
   const mainSectionSlug = fullUrl?.split("/")[1];
   const tagSectionSlug = fullUrl?.split("/")[2];

   if (isNil(rootNodeSlug)) {
      return "Unable to determine current root node";
   }

   if (!isDataAvailable(contentStore.urlData) || !isDataAvailable(contentStore.sections)) {
      return null;
   }

   const rootNode = contentStore.urlData.data.find((urlData) => urlData.fullUrl === rootNodeSlug);
   const mainSections = contentStore.sections.data.filter((section) => section.fields.parentId === rootNode?.id);

   const selectedMainSection = mainSections.find((section) => section.fields.slug === mainSectionSlug);

   const tags = selectedMainSection
      ? contentStore.sections.data.filter((section) => section.fields.parentId === selectedMainSection.sys.id)
      : [];
   const selectedTagSection = tags.find((section) => section.fields.slug === tagSectionSlug);

   return (
      <Stack gap={6} className="w-full" align="start">
         <Group gap={4} className="mb-3" justify="start" wrap="wrap">
            <ChipLink label="Alle artikler" active={isNil(selectedMainSection)} url={`/${rootNode?.fullUrl}`} />
            {mainSections.map((section) => {
               const sectionId = section.sys.id || "";
               const urlData = contentStore.urlData.data.find((urlData) => urlData.id === sectionId);
               if (!sectionHasContent(section.sys.id) || !urlData) {
                  return null;
               }
               return (
                  <ChipLink
                     key={section.sys.id}
                     label={section.fields.title}
                     active={section.fields.slug === mainSectionSlug}
                     url={`/${urlData?.fullUrl}`}
                  />
               );
            })}
         </Group>
         <h2 className="text-xl lg:text-2xl">
            {selectedMainSection?.fields.title ? selectedMainSection.fields.title : "Alle artikler"}
         </h2>
         {tags.length > 0 && (
            <Group className="mb-5" justify="start" gap={2} wrap="wrap">
               <ChipLink
                  label="Alle"
                  active={isNil(selectedTagSection)}
                  url={`/${rootNode?.fullUrl}/${selectedMainSection?.fields.slug}`}
               />
               {tags.map((tag) => {
                  const sectionId = tag?.sys.id || "";
                  const urlData = contentStore.urlData.data.find((urlData) => urlData.id === sectionId);
                  if (!sectionHasContent(tag.sys.id) || !urlData) {
                     return null;
                  }

                  return (
                     <ChipLink
                        key={tag.sys.id}
                        label={tag?.fields.title}
                        active={tag.fields.slug === tagSectionSlug}
                        url={`/${urlData?.fullUrl}`}
                     />
                  );
               })}
            </Group>
         )}
      </Stack>
   );
};

export default observer(SectionNavigator);
